import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  FileSpreadsheet,
  FolderUp,
  ListPlus,
  Loader,
  Upload,
} from 'lucide-react';
import { EmptyState } from '../../components/ui/EmptyState';
import { Pill } from '../../components/ui/Pill';
import type { Loan } from '../../graphql/types';
import { formatFileCount, getLoanTitle } from '../LoanRail';
import { useWorkflowWorkspace } from '../WorkflowWorkspace';
import { writeWorkspaceParams } from '../urlState';
import { clearDraftTape, peekDraftTape } from '../draftHandoff';
import * as intakeData from './intakeDDData';
import {
  ACCEPTED_FILE_TYPES,
  LOAN_FIELDS,
  SUPPORTED_FILE_TYPES_DISPLAY,
  deriveLoans,
  extractFileCount,
  fieldForColumn,
  fileNameStem,
  formatDocCount,
  formatExtractLabel,
  formatMappingCounts,
  guessMapping,
  incompleteLoanIds,
  isIncompleteFilterOn,
  isSupportedDocument,
  loanDocCompleteness,
  loanFileTotal,
  mappingCounts,
  matchFolderToLoan,
  parseManualLoans,
  setColumnField,
  withIncompleteFilter,
} from './intakeDDModel';
import type { ColumnMapping, DraftLoan, LoanField, Tape } from './intakeDDModel';
import {
  loadPendingUploads,
  loadStoredTape,
  savePendingUploads,
  saveStoredTape,
} from './intakeDDStore';
import type { PendingUploads, StoredTape } from './intakeDDStore';

/**
 * IntakeDD: the DD Intake Mode body, built from boards `6a`, `6b` and `5a`.
 *
 * Stage 1 builds the loan list, from a tape (XLSX/CSV) or by hand; both give
 * the same list. A tape always passes through the column mapping view before
 * any loan is created, and the mapping stays reachable from "Review column
 * mapping". Changing a mapping updates the loan list shown beside it, and
 * "Apply mapping" writes it to the loans.
 *
 * Stage 2 is the per-loan document table (Loan number, Borrower, Documents).
 * It is disabled until the loan list exists and ends in "Extract N files".
 * Each row is a drop target for files and has an "Add files" button. The
 * Documents cell shows the file count (Requirement 4.4) and, when the required
 * document types of the chosen or default guideline are known, "x of y docs"
 * (red when incomplete, green when complete). Without a required set it shows
 * the file count alone.
 *
 * Rows with missing documents can be selected from outside: `?loan=<id>`
 * highlights that row, and `?docs=incomplete` limits the table to loans with
 * missing documents (`withIncompleteFilter`, `incompleteLoanIds`). The Rail
 * count note "n need you" (task 4.2) can link there.
 *
 * The job comes from `useWorkflowWorkspace()`.
 *
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5.
 */
export interface IntakeDDProps {
  /** Called after files are sent to extraction. Defaults to opening Extract mode. */
  onExtract?: () => void;
}

interface MappingEdit {
  tape: Tape;
  mapping: ColumnMapping;
  /** True once loans exist for this tape (reopened, or partly created). */
  committed: boolean;
  rowLoanIds: Record<string, string>;
}

const OVERLINE = 'text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500';
const CARD = 'rounded-xl border border-gray-200 bg-white p-[18px]';
const PRIMARY_BUTTON =
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[9px] bg-[#834DFB] px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#6E2EF0] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[#834DFB]';
const SECONDARY_BUTTON =
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-[13px] font-bold text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white';
const ACCENT_BUTTON =
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-[#c4b5fd] bg-white px-3.5 py-2 text-[13px] font-bold text-[#5A21C9] transition-colors hover:bg-[#F5F3FF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB]';

const NOT_STARTED = new Set(['', 'NOT_STARTED', 'PENDING']);

function stepWasStarted(status: string | null | undefined): boolean {
  return !NOT_STARTED.has((status ?? '').toString().toUpperCase());
}

function sameLoanInfo(loan: Loan, draft: DraftLoan): boolean {
  return (
    (loan.loanNumber ?? '') === draft.loanNumber &&
    (loan.borrowerName ?? '') === draft.borrowerName &&
    (loan.propertyAddress ?? '') === draft.propertyAddress &&
    (loan.loanAmount ?? '') === draft.loanAmount
  );
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

export const IntakeDD: React.FC<IntakeDDProps> = ({ onExtract }) => {
  const {
    job,
    jobType,
    reload,
    data: workspaceData,
    refreshData,
    reportStatus,
  } = useWorkflowWorkspace();
  const [searchParams, setSearchParams] = useSearchParams();
  const jobId = job.jobId;

  // ── Loans ─────────────────────────────────────────────────────────────────
  const [loans, setLoans] = useState<Loan[]>([]);
  const [loansLoaded, setLoansLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const refreshLoans = useCallback(async () => {
    try {
      setLoans(await intakeData.loadLoans(jobId));
      setLoadError(null);
    } catch (err) {
      console.error('[IntakeDD] Failed to load loans:', err);
      setLoadError('The loan list could not be loaded. Check your connection and try again.');
    } finally {
      setLoansLoaded(true);
    }
  }, [jobId]);

  useEffect(() => {
    setLoansLoaded(false);
    void refreshLoans();
  }, [refreshLoans]);

  // ── Tape, mapping and manual entry ────────────────────────────────────────
  const [stored, setStored] = useState<StoredTape | null>(() => loadStoredTape(jobId));
  // A tape chosen in the draft Workspace opens straight in the column mapping.
  const [editing, setEditing] = useState<MappingEdit | null>(() => {
    const tape = peekDraftTape(jobId);
    return tape
      ? { tape, mapping: guessMapping(tape.columns), committed: false, rowLoanIds: {} }
      : null;
  });
  useEffect(() => {
    clearDraftTape(jobId);
  }, [jobId]);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualText, setManualText] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [dropActive, setDropActive] = useState(false);

  const tapeInputRef = useRef<HTMLInputElement>(null);

  const derived = useMemo(
    () => (editing ? deriveLoans(editing.tape, editing.mapping) : null),
    [editing]
  );
  const manual = useMemo(() => parseManualLoans(manualText), [manualText]);

  const openTape = async (file: File | undefined) => {
    if (!file) return;
    setNotice(null);
    setBusy(true);
    try {
      const tape = await intakeData.parseTapeFile(file);
      setEditing({ tape, mapping: guessMapping(tape.columns), committed: false, rowLoanIds: {} });
      setManualOpen(false);
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'That file could not be read.');
    } finally {
      setBusy(false);
    }
  };

  const reviewMapping = () => {
    if (!stored) return;
    setNotice(null);
    setEditing({
      tape: stored.tape,
      mapping: stored.mapping,
      committed: true,
      rowLoanIds: stored.rowLoanIds,
    });
  };

  const changeColumn = (columnIndex: number, field: LoanField | null) => {
    setEditing((prev) =>
      prev ? { ...prev, mapping: setColumnField(prev.mapping, columnIndex, field) } : prev
    );
  };

  /** Create the loans for new rows and update the loans whose row changed. */
  const applyMapping = async () => {
    if (!editing || !derived || derived.loans.length === 0) return;
    setBusy(true);
    setNotice(null);
    const rowLoanIds = { ...editing.rowLoanIds };
    const persist = () => {
      const next: StoredTape = { tape: editing.tape, mapping: editing.mapping, rowLoanIds };
      saveStoredTape(jobId, next);
      setStored(next);
    };
    try {
      const toCreate: DraftLoan[] = [];
      for (const draft of derived.loans) {
        const loanId = rowLoanIds[String(draft.rowIndex)];
        if (!loanId) {
          toCreate.push(draft);
          continue;
        }
        const existing = loans.find((l) => l.loanId === loanId);
        if (!existing || !sameLoanInfo(existing, draft)) {
          await intakeData.updateLoan(jobId, loanId, draft);
        }
      }
      await intakeData.createLoans(jobId, toCreate, ({ rowIndex, loan }) => {
        rowLoanIds[String(rowIndex)] = loan.loanId;
      });
      persist();
      setEditing(null);
      await refreshLoans();
      void reload();
      void refreshData();
    } catch (err) {
      console.error('[IntakeDD] Failed to apply the mapping:', err);
      // Keep what was created, so Apply picks up where it stopped.
      if (Object.keys(rowLoanIds).length > 0) {
        persist();
        setEditing((prev) => (prev ? { ...prev, committed: true, rowLoanIds } : prev));
        await refreshLoans();
      }
      setNotice('Some loans could not be saved. Check your connection, then choose Apply mapping again.');
    } finally {
      setBusy(false);
    }
  };

  const addManualLoans = async () => {
    const known = new Set(loans.map((l) => (l.loanNumber ?? '').trim().toLowerCase()));
    const fresh = manual.loans.filter((l) => !known.has(l.loanNumber.toLowerCase()));
    if (fresh.length === 0) return;
    setBusy(true);
    setNotice(null);
    try {
      await intakeData.createLoans(jobId, fresh);
      setManualText('');
      setManualOpen(false);
      await refreshLoans();
      void reload();
      void refreshData();
    } catch (err) {
      console.error('[IntakeDD] Failed to add loans:', err);
      setNotice('The loans could not be added. Check your connection and try again.');
      await refreshLoans();
    } finally {
      setBusy(false);
    }
  };

  // ── Documents ─────────────────────────────────────────────────────────────
  const [pending, setPending] = useState<PendingUploads>(() => loadPendingUploads(jobId));
  const [uploading, setUploading] = useState<Record<string, number>>({});
  const [dragOverLoan, setDragOverLoan] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [documentError, setDocumentError] = useState<string | null>(null);

  const folderInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileTargetRef = useRef<Loan | null>(null);

  const uploadFiles = async (files: File[], loan: Loan) => {
    const accepted = files.filter((f) => isSupportedDocument(f.name));
    const rejected = files.length - accepted.length;
    const problems: string[] = [];
    if (rejected > 0) {
      problems.push(
        `${plural(rejected, 'file was', 'files were')} skipped. Supported types: ${SUPPORTED_FILE_TYPES_DISPLAY}.`
      );
    }
    if (accepted.length > 0) {
      setUploading((prev) => ({ ...prev, [loan.loanId]: (prev[loan.loanId] ?? 0) + accepted.length }));
      let failed = 0;
      for (const file of accepted) {
        try {
          await intakeData.uploadLoanFile(job, loan.loanId, file);
          setPending((prev) => {
            const names = prev[loan.loanId] ?? [];
            const next = names.includes(file.name)
              ? prev
              : { ...prev, [loan.loanId]: [...names, file.name] };
            savePendingUploads(jobId, next);
            return next;
          });
        } catch (err) {
          console.error('[IntakeDD] Upload failed:', err);
          failed += 1;
        } finally {
          setUploading((prev) => ({ ...prev, [loan.loanId]: Math.max(0, (prev[loan.loanId] ?? 1) - 1) }));
        }
      }
      if (failed > 0) {
        problems.push(
          `${plural(failed, 'file', 'files')} for ${getLoanTitle(loan)} could not be uploaded. Try adding ${failed === 1 ? 'it' : 'them'} again.`
        );
      }
    }
    setDocumentError(problems.length > 0 ? problems.join(' ') : null);
  };

  const uploadFolder = async (files: File[]) => {
    const byLoan = new Map<string, { loan: Loan; files: File[] }>();
    let unmatched = 0;
    for (const file of files) {
      const path = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
      const loan = matchFolderToLoan(path, loans);
      if (!loan) {
        unmatched += 1;
        continue;
      }
      const entry = byLoan.get(loan.loanId) ?? { loan, files: [] };
      entry.files.push(file);
      byLoan.set(loan.loanId, entry);
    }
    for (const { loan, files: loanFiles } of byLoan.values()) {
      await uploadFiles(loanFiles, loan);
    }
    if (unmatched > 0) {
      setDocumentError(
        `${plural(unmatched, 'file', 'files')} did not sit in a folder named by loan number and ${unmatched === 1 ? 'was' : 'were'} skipped.`
      );
    }
  };

  // Required document types of the chosen or default guideline, when known.
  const [requiredInfo, setRequiredInfo] = useState<intakeData.RequiredDocsInfo | null>(null);
  useEffect(() => {
    let cancelled = false;
    void intakeData.loadRequiredDocs(jobId).then((info) => {
      if (!cancelled) setRequiredInfo(info ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  const required = requiredInfo?.required ?? [];
  const presentNames = (loanId: string): string[] => [
    ...(requiredInfo?.typesByLoan[loanId] ?? []),
    ...(pending[loanId] ?? []).map(fileNameStem),
  ];
  const incompleteIds = incompleteLoanIds(
    loans,
    required,
    Object.fromEntries(loans.map((l) => [l.loanId, presentNames(l.loanId)]))
  );
  const filterOn = isIncompleteFilterOn(searchParams) && incompleteIds.length > 0;
  const visibleLoans = filterOn ? loans.filter((l) => incompleteIds.includes(l.loanId)) : loans;
  const selectedLoanId = searchParams.get('loan');

  // Bring a row chosen from outside (the Rail) into view.
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  useEffect(() => {
    if (selectedLoanId) rowRefs.current[selectedLoanId]?.scrollIntoView?.({ block: 'nearest' });
  }, [selectedLoanId, loans.length]);

  const total = extractFileCount(pending);

  // Top_Bar "Intake · 96 files": files already on the job plus those waiting to
  // be extracted. Only Intake knows about the waiting ones, so it reports this.
  const filesOnJob = workspaceData.status === 'ready' ? workspaceData.data.files.length : 0;
  const intakeFileCount = filesOnJob + total;
  useEffect(() => {
    if (intakeFileCount > 0) reportStatus({ fileCount: intakeFileCount });
  }, [intakeFileCount, reportStatus]);

  const anyUploading = Object.values(uploading).some((n) => n > 0);
  const hasLoans = loans.length > 0;
  const canExtract = hasLoans && total > 0 && !anyUploading && !submitting;
  const extractHint = !hasLoans
    ? 'Add loans first. Documents unlock once the loan list exists.'
    : total === 0
      ? 'Add files to at least one loan to extract.'
      : anyUploading
        ? 'Wait for the uploads to finish.'
        : undefined;

  const handleExtract = async () => {
    if (!canExtract) return;
    setSubmitting(true);
    setDocumentError(null);
    try {
      await intakeData.submitForExtraction(job, jobType, stepWasStarted(job.documentProcessingStatus));
      setPending({});
      savePendingUploads(jobId, {});
      await reload();
      if (onExtract) onExtract();
      else setSearchParams(writeWorkspaceParams(searchParams, { mode: 'extract' }));
    } catch (err) {
      console.error('[IntakeDD] Failed to start extraction:', err);
      setDocumentError('Extraction could not be started. Check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Mapping view (board 6b) ───────────────────────────────────────────────
  if (editing && derived) {
    const counts = mappingCounts(editing.mapping, editing.tape.columns.length);
    const preview = derived.loans.slice(0, 8);
    const skipped = [
      derived.blank > 0 ? `${plural(derived.blank, 'row has', 'rows have')} no loan number` : null,
      derived.duplicates > 0
        ? `${plural(derived.duplicates, 'row repeats', 'rows repeat')} an earlier loan number`
        : null,
    ].filter(Boolean);
    const ready = derived.loans.length > 0 && !busy;
    const actionLabel = editing.committed
      ? 'Apply mapping'
      : `Create ${plural(derived.loans.length, 'loan', 'loans')}`;

    return (
      <div data-testid="intake-dd" className="flex max-w-[1100px] flex-col gap-4">
        <div className={`${CARD} flex items-center gap-4`}>
          <TapeBadge fileName={editing.tape.fileName} />
          <div className="min-w-0 flex-1">
            <p className={OVERLINE}>Stage 1 · Column mapping</p>
            <h2 className="mt-0.5 truncate text-base font-bold text-gray-900" title={editing.tape.fileName}>
              {editing.tape.fileName}
            </h2>
            <p data-testid="mapping-counts" className="text-[13px] text-gray-500">
              {plural(editing.tape.rows.length, 'row', 'rows')} · {formatMappingCounts(counts)}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-[minmax(0,1fr)_340px] gap-4">
          <section aria-label="Tape columns" className={`${CARD} p-0`}>
            <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_180px] gap-3 border-b border-gray-100 px-4 py-2.5">
              <span className={OVERLINE}>Tape column</span>
              <span className={OVERLINE}>Example values</span>
              <span className={OVERLINE}>Maps to</span>
            </div>
            <ul>
              {editing.tape.columns.map((name, index) => {
                const field = fieldForColumn(editing.mapping, index);
                const samples = editing.tape.rows
                  .map((r) => r[index])
                  .filter(Boolean)
                  .slice(0, 2)
                  .join(' · ');
                return (
                  <li
                    key={index}
                    className={`grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_180px] items-center gap-3 border-b border-gray-50 px-4 py-2 ${
                      field ? 'bg-[#F5F3FF]' : ''
                    }`}
                  >
                    <span className="truncate text-sm font-bold text-gray-900" title={name}>
                      {name}
                    </span>
                    <span className="truncate text-[13px] text-gray-500" title={samples}>
                      {samples || '—'}
                    </span>
                    <select
                      aria-label={`Map column ${name}`}
                      value={field ?? ''}
                      onChange={(e) => changeColumn(index, (e.target.value || null) as LoanField | null)}
                      className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-[13px] text-gray-900 focus:border-[#834DFB] focus:outline-none focus:ring-2 focus:ring-[#834DFB]/30"
                    >
                      <option value="">Ignore</option>
                      {LOAN_FIELDS.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.label}
                        </option>
                      ))}
                    </select>
                  </li>
                );
              })}
            </ul>
          </section>

          <section aria-label="Loan list" className={`${CARD} h-fit`}>
            <p className={OVERLINE}>Loan list</p>
            <p data-testid="derived-count" aria-live="polite" className="mt-0.5 text-base font-bold text-gray-900">
              {plural(derived.loans.length, 'loan', 'loans')}
            </p>
            {editing.mapping.loanNumber === null ? (
              <p role="status" className="mt-2 rounded-lg border border-[#fef08a] bg-[#fefce8] p-2.5 text-xs text-[#854d0e]">
                Map one column to Loan number to build the loan list.
              </p>
            ) : (
              <ul className="mt-2 divide-y divide-gray-100">
                {preview.map((l) => (
                  <li key={l.rowIndex} className="py-1.5">
                    <span className="block truncate text-sm font-bold text-gray-900">{l.loanNumber}</span>
                    {l.borrowerName && (
                      <span className="block truncate text-xs text-gray-500">{l.borrowerName}</span>
                    )}
                  </li>
                ))}
                {derived.loans.length > preview.length && (
                  <li className="py-1.5 text-xs text-gray-500">
                    and {derived.loans.length - preview.length} more
                  </li>
                )}
              </ul>
            )}
            {skipped.length > 0 && (
              <p className="mt-2 text-xs text-gray-500">Skipped: {skipped.join('; ')}.</p>
            )}
          </section>
        </div>

        {notice && <Notice>{notice}</Notice>}

        <div className="flex items-center justify-end gap-2">
          <button type="button" className={SECONDARY_BUTTON} disabled={busy} onClick={() => setEditing(null)}>
            {editing.committed ? (
              <>
                <ArrowLeft size={14} strokeWidth={2} aria-hidden="true" />
                Back to loans
              </>
            ) : (
              'Cancel'
            )}
          </button>
          <button
            type="button"
            className={PRIMARY_BUTTON}
            disabled={!ready}
            title={derived.loans.length === 0 ? 'Map a Loan number column first.' : undefined}
            onClick={() => void applyMapping()}
          >
            {busy ? (
              <>
                <Loader size={14} strokeWidth={2} className="animate-spin" aria-hidden="true" />
                Saving
              </>
            ) : (
              actionLabel
            )}
          </button>
        </div>
      </div>
    );
  }

  // ── Stage 1 + Stage 2 ─────────────────────────────────────────────────────
  const showEmpty = loansLoaded && !hasLoans && !loadError;

  return (
    <div data-testid="intake-dd" className="flex max-w-[1100px] flex-col gap-4">
      <input
        ref={tapeInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        aria-label="Loan tape file"
        className="sr-only"
        tabIndex={-1}
        onChange={(e) => {
          void openTape(e.target.files?.[0]);
          e.target.value = '';
        }}
      />

      {loadError && (
        <Notice>
          {loadError}{' '}
          <button type="button" className="font-bold underline" onClick={() => void refreshLoans()}>
            Retry
          </button>
        </Notice>
      )}

      {!loansLoaded && !loadError && (
        <div className={`${CARD} flex items-center gap-2 text-sm text-gray-600`} role="status">
          <Loader size={16} strokeWidth={2} className="animate-spin" aria-hidden="true" />
          Loading loans
        </div>
      )}

      {showEmpty && (
        <div
          data-testid="intake-empty"
          onDragOver={(e) => {
            e.preventDefault();
            setDropActive(true);
          }}
          onDragLeave={() => setDropActive(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDropActive(false);
            void openTape(e.dataTransfer.files[0]);
          }}
          className={`rounded-[14px] ${dropActive ? 'bg-[#F5F3FF]' : ''}`}
        >
          <p className={`${OVERLINE} mb-2`}>Stage 1 · Loan list</p>
          <EmptyState
            icon={<Upload size={20} strokeWidth={2} aria-hidden="true" />}
            title="Drop a loan tape, or add loans by hand"
            description="XLSX or CSV. You'll check the column mapping before anything is extracted. Documents unlock once the loan list exists."
            action={
              <>
                <button type="button" className={PRIMARY_BUTTON} disabled={busy} onClick={() => tapeInputRef.current?.click()}>
                  {busy ? (
                    <Loader size={14} strokeWidth={2} className="animate-spin" aria-hidden="true" />
                  ) : null}
                  Choose tape file
                </button>
                <button
                  type="button"
                  className={SECONDARY_BUTTON}
                  aria-expanded={manualOpen}
                  onClick={() => setManualOpen((open) => !open)}
                >
                  Add loans manually
                </button>
              </>
            }
          />
        </div>
      )}

      {hasLoans && (
        <div className={`${CARD} flex items-center gap-4`}>
          {stored ? <TapeBadge fileName={stored.tape.fileName} /> : <ManualBadge />}
          <div className="min-w-0 flex-1">
            <p className={OVERLINE}>{stored ? 'Stage 1 · Loan tape' : 'Stage 1 · Loan list'}</p>
            <h2
              className="mt-0.5 truncate text-base font-bold text-gray-900"
              title={stored?.tape.fileName}
            >
              {stored ? stored.tape.fileName : 'Loans added by hand'}
            </h2>
            <p data-testid="tape-summary" className="text-[13px] text-gray-500">
              {plural(loans.length, 'loan', 'loans')}
              {stored &&
                ` · ${formatMappingCounts(mappingCounts(stored.mapping, stored.tape.columns.length))}`}
            </p>
          </div>
          {stored && (
            <button type="button" className={ACCENT_BUTTON} onClick={reviewMapping}>
              Review column mapping
            </button>
          )}
          <button
            type="button"
            className={SECONDARY_BUTTON}
            aria-expanded={manualOpen}
            onClick={() => setManualOpen((open) => !open)}
          >
            <ListPlus size={14} strokeWidth={2} aria-hidden="true" />
            Add loans
          </button>
        </div>
      )}

      {manualOpen && (
        <section aria-label="Add loans manually" className={CARD}>
          <label htmlFor="manual-loans" className="text-sm font-bold text-gray-900">
            One loan per line
          </label>
          <p className="mt-0.5 text-xs text-gray-500">
            Type the loan number, then a comma and the borrower name if you have it. For example: LN-1001, Jane Doe
          </p>
          <textarea
            id="manual-loans"
            value={manualText}
            onChange={(e) => setManualText(e.target.value)}
            rows={5}
            className="mt-2 w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-[#834DFB] focus:outline-none focus:ring-2 focus:ring-[#834DFB]/30"
          />
          <div className="mt-2 flex items-center justify-between gap-3">
            <p data-testid="manual-count" aria-live="polite" className="text-xs text-gray-500">
              {plural(manual.loans.length, 'loan', 'loans')}
              {manual.duplicates > 0 && ` · ${plural(manual.duplicates, 'repeat', 'repeats')} skipped`}
            </p>
            <button
              type="button"
              className={PRIMARY_BUTTON}
              disabled={busy || manual.loans.length === 0}
              onClick={() => void addManualLoans()}
            >
              {busy ? (
                <Loader size={14} strokeWidth={2} className="animate-spin" aria-hidden="true" />
              ) : null}
              {hasLoans ? 'Add loans' : `Create ${plural(manual.loans.length, 'loan', 'loans')}`}
            </button>
          </div>
        </section>
      )}

      {notice && <Notice>{notice}</Notice>}

      <section aria-label="Documents per loan" className={CARD}>
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className={OVERLINE}>Stage 2 · Documents per loan</p>
            <h2 className="mt-0.5 text-base font-bold text-gray-900">
              {hasLoans
                ? 'Drop files onto a loan, or add a folder named by loan number'
                : 'Documents unlock once the loan list exists'}
            </h2>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              className={SECONDARY_BUTTON}
              disabled={!hasLoans}
              title={hasLoans ? undefined : extractHint}
              onClick={() => folderInputRef.current?.click()}
            >
              <FolderUp size={14} strokeWidth={2} aria-hidden="true" />
              Add folder
            </button>
            <button
              type="button"
              data-testid="extract-action"
              className={PRIMARY_BUTTON}
              disabled={!canExtract}
              title={extractHint}
              onClick={() => void handleExtract()}
            >
              {submitting ? (
                <>
                  <Loader size={14} strokeWidth={2} className="animate-spin" aria-hidden="true" />
                  Starting
                </>
              ) : (
                <>
                  {formatExtractLabel(total)}
                  <ArrowRight size={14} strokeWidth={2} aria-hidden="true" />
                </>
              )}
            </button>
          </div>
        </div>

        <input
          ref={folderInputRef}
          type="file"
          multiple
          aria-label="Folder of loan documents"
          className="sr-only"
          tabIndex={-1}
          {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
          onChange={(e) => {
            void uploadFolder(Array.from(e.target.files ?? []));
            e.target.value = '';
          }}
        />
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPTED_FILE_TYPES}
          aria-label="Loan documents"
          className="sr-only"
          tabIndex={-1}
          onChange={(e) => {
            const loan = fileTargetRef.current;
            if (loan) void uploadFiles(Array.from(e.target.files ?? []), loan);
            e.target.value = '';
          }}
        />

        {documentError && (
          <div className="mt-3">
            <Notice>{documentError}</Notice>
          </div>
        )}

        {incompleteIds.length > 0 && (
          <div className="mt-3 flex items-center justify-between gap-3" data-testid="incomplete-bar">
            <Pill tone="danger">
              {plural(incompleteIds.length, 'loan is', 'loans are')} missing documents
            </Pill>
            <button
              type="button"
              className={SECONDARY_BUTTON}
              aria-pressed={filterOn}
              onClick={() => setSearchParams(withIncompleteFilter(searchParams, !filterOn))}
            >
              {filterOn ? 'Show all loans' : 'Show only these'}
            </button>
          </div>
        )}

        <div className="mt-3.5 overflow-hidden rounded-[10px] border border-gray-100" role="table" aria-label="Loans and files">
          <div role="row" className="grid grid-cols-[1.2fr_2fr_1.2fr_auto] items-center gap-3.5 border-b border-gray-100 bg-[#f9fafb] py-2 pl-[17px] pr-3.5">
            <span role="columnheader" className={OVERLINE}>Loan number</span>
            <span role="columnheader" className={OVERLINE}>Borrower</span>
            <span role="columnheader" className={OVERLINE}>Documents</span>
            <span role="columnheader" className="sr-only">Actions</span>
          </div>
          {hasLoans ? (
            visibleLoans.map((loan) => {
              const count = loanFileTotal(loan.fileCount, (pending[loan.loanId] ?? []).length);
              const busyCount = uploading[loan.loanId] ?? 0;
              const docs = loanDocCompleteness(required, presentNames(loan.loanId));
              const dropping = dragOverLoan === loan.loanId;
              const selected = selectedLoanId === loan.loanId;
              const title = getLoanTitle(loan);
              return (
                <div
                  key={loan.loanId}
                  ref={(el) => {
                    rowRefs.current[loan.loanId] = el;
                  }}
                  role="row"
                  data-testid="loan-row"
                  data-drop-target={dropping ? 'true' : undefined}
                  aria-selected={selected || undefined}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOverLoan(loan.loanId);
                  }}
                  onDragLeave={(e) => {
                    // Moving over a child fires dragleave on the row; only clear on a real exit.
                    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragOverLoan(null);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOverLoan(null);
                    void uploadFiles(Array.from(e.dataTransfer.files), loan);
                  }}
                  className={`grid grid-cols-[1.2fr_2fr_1.2fr_auto] items-center gap-3.5 border-b border-l-[3px] border-gray-50 py-2.5 pl-3.5 pr-3.5 ${
                    dropping
                      ? 'border-l-[#834DFB] bg-[#F5F3FF] outline-dashed outline-2 -outline-offset-2 outline-[#c4b5fd]'
                      : selected
                        ? 'border-l-[#834DFB] bg-[#F5F3FF]'
                        : 'border-l-transparent'
                  }`}
                >
                  <span role="cell" className="truncate text-sm font-bold text-gray-900">
                    {title}
                  </span>
                  <span role="cell" className="truncate text-[13px] text-gray-600">
                    {loan.borrowerName || '—'}
                  </span>
                  <span role="cell" className="min-w-0 text-[13px]">
                    {dropping ? (
                      <span data-testid="drop-hint" className="font-bold text-[#5A21C9]">
                        Drop to add to {title}
                      </span>
                    ) : (
                      <>
                        {docs && (
                          <span
                            data-testid="loan-docs"
                            title={docs.complete ? undefined : `Missing: ${docs.missing.join(', ')}`}
                            className={`block font-bold ${docs.complete ? 'text-[#166534]' : 'text-[#991b1b]'}`}
                          >
                            {formatDocCount(docs)}
                          </span>
                        )}
                        <span
                          data-testid="loan-files"
                          className={
                            docs
                              ? 'block text-xs text-gray-500'
                              : `font-bold ${count > 0 ? 'text-[#166534]' : 'text-[#854d0e]'}`
                          }
                        >
                          {formatFileCount(count)}
                          {busyCount > 0 && (
                            <span className="ml-1.5 font-normal text-gray-500">
                              <Loader size={12} strokeWidth={2} className="mr-1 inline animate-spin" aria-hidden="true" />
                              uploading {busyCount}
                            </span>
                          )}
                        </span>
                      </>
                    )}
                  </span>
                  <span role="cell">
                    <button
                      type="button"
                      className={SECONDARY_BUTTON}
                      aria-label={`Add files to ${getLoanTitle(loan)}`}
                      onClick={() => {
                        fileTargetRef.current = loan;
                        fileInputRef.current?.click();
                      }}
                    >
                      <Upload size={14} strokeWidth={2} aria-hidden="true" />
                      Add files
                    </button>
                  </span>
                </div>
              );
            })
          ) : (
            <p data-testid="documents-locked" className="px-3.5 py-6 text-center text-sm text-gray-500">
              No loans yet. Add a tape or loans by hand to attach documents.
            </p>
          )}
        </div>
        {total > 0 && (
          <p className="mt-2 text-xs text-gray-500">
            <Pill tone="selected">{plural(total, 'new file', 'new files')}</Pill> will be extracted. Files already
            extracted stay as they are.
          </p>
        )}
      </section>
    </div>
  );
};

const TapeBadge: React.FC<{ fileName: string }> = ({ fileName }) => (
  <span
    aria-hidden="true"
    className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-[#EDE7FF] text-xs font-bold text-[#5A21C9]"
  >
    {/\.csv$/i.test(fileName) ? 'CSV' : 'XLS'}
  </span>
);

const ManualBadge: React.FC = () => (
  <span
    aria-hidden="true"
    className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-[#EDE7FF] text-[#5A21C9]"
  >
    <FileSpreadsheet size={18} strokeWidth={2} />
  </span>
);

const Notice: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    role="alert"
    className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-[13px] text-[#991b1b]"
  >
    <AlertCircle size={16} strokeWidth={2} className="mt-px shrink-0" aria-hidden="true" />
    <p>{children}</p>
  </div>
);

export default IntakeDD;
